import {
  CardNumberElement,
  CardMonthElement,
  CardYearElement,
  CardCvvElement,
} from 'lib/recurly'
import amex from 'styles/assets/cards/card_amex.svg'
import discover from 'styles/assets/cards/card_discover.svg'
import mastercard from 'styles/assets/cards/card_master.svg'
import visa from 'styles/assets/cards/card_visa.svg'
import diners from 'styles/assets/cards/card_diners.png'
import jcb from 'styles/assets/cards/card_jcb.svg'
import unionpay from 'styles/assets/cards/card_unionpay.svg'
import cartes_bancaires from 'styles/assets/cards/card_cartebancaire.svg'

import { CheckoutFormValues } from '../schemas'
import { filterCountries } from '../utils'

export const THREE_D_ELEMENT_ID = 'new-checkout-3d-auth-container'

// eslint-disable-next-line
export enum RECURLY_FORM_FIELDS {
  FIRST_NAME = 'first_name',
  LAST_NAME = 'last_name',
  CARD_NUMBER = 'number',
  EXPIRY_MONTH = 'month',
  EXPIRY_YEAR = 'year',
  CVV = 'cvv',
  POSTAL_CODE = 'postal_code',
  COUNTRY = 'country',
}

export const FLAG_STYLES = {
  width: '100%',
  height: '100%',
} satisfies React.CSSProperties

export interface FieldNames {
  [RECURLY_FORM_FIELDS.FIRST_NAME]: 'firstName'
  [RECURLY_FORM_FIELDS.LAST_NAME]: 'lastName'
  [RECURLY_FORM_FIELDS.CARD_NUMBER]: 'cardNumber'
  [RECURLY_FORM_FIELDS.EXPIRY_MONTH]: 'expiryMonth'
  [RECURLY_FORM_FIELDS.EXPIRY_YEAR]: 'expiryYear'
  [RECURLY_FORM_FIELDS.CVV]: 'cvv'
  [RECURLY_FORM_FIELDS.POSTAL_CODE]: 'postalCode'
  [RECURLY_FORM_FIELDS.COUNTRY]: 'country'
}

export const FIELD_NAMES: FieldNames = {
  [RECURLY_FORM_FIELDS.FIRST_NAME]: 'firstName',
  [RECURLY_FORM_FIELDS.LAST_NAME]: 'lastName',
  [RECURLY_FORM_FIELDS.CARD_NUMBER]: 'cardNumber',
  [RECURLY_FORM_FIELDS.EXPIRY_MONTH]: 'expiryMonth',
  [RECURLY_FORM_FIELDS.EXPIRY_YEAR]: 'expiryYear',
  [RECURLY_FORM_FIELDS.CVV]: 'cvv',
  [RECURLY_FORM_FIELDS.POSTAL_CODE]: 'postalCode',
  [RECURLY_FORM_FIELDS.COUNTRY]: 'country',
}

export const ERROR_MESSAGES = {
  firstName: 'alert_required',
  lastName: 'alert_required',
  cardNumber: 'alert_invalid_card',
  expiryMonth: 'alert_invalid',
  expiryYear: 'alert_expired',
  cvv: 'alert_invalid',
  postalCode: 'alert_invalid',
  country: 'alert_required',
} as const

interface ComboBoxValues {
  country: ReturnType<typeof filterCountries>
}

export const COMBO_BOX_VALUES: ComboBoxValues = {
  country: filterCountries('') ?? [],
}

export type CardElementComponent =
  | typeof CardNumberElement
  | typeof CardMonthElement
  | typeof CardYearElement
  | typeof CardCvvElement

export interface InputConfig {
  type: 'input' | 'fieldWrapper' | 'controller' | 'parent'
  name?: keyof CheckoutFormValues
  label?: string
  placeholder?: string
  dataRecurly?: string
  Component?: CardElementComponent
  className?: string
  fieldWrapperClass?: string
}

interface RecurlyInputs {
  [key: string]: InputConfig
}

export const RECURLY_INPUTS: RecurlyInputs = {
  firstName: {
    type: 'input',
    name: 'firstName',
    dataRecurly: RECURLY_FORM_FIELDS.FIRST_NAME,
    label: 'first_name_lowercase',
    placeholder: 'first_name_lowercase',
  },
  lastName: {
    type: 'input',
    name: 'lastName',
    dataRecurly: RECURLY_FORM_FIELDS.LAST_NAME,
    label: 'last_name_lowercase',
    placeholder: 'last_name_lowercase',
  },
  cardNumber: {
    type: 'fieldWrapper',
    name: 'cardNumber',
    Component: CardNumberElement,
    label: 'card_number_lowercase',
    placeholder: 'card_number_placeholder',
  },
  expiryMonth: {
    type: 'fieldWrapper',
    name: 'expiryMonth',
    Component: CardMonthElement,
    label: 'month_value',
    placeholder: 'mm',
    className: 'tw-max-w-[140px]',
  },
  expiryYear: {
    type: 'fieldWrapper',
    name: 'expiryYear',
    Component: CardYearElement,
    label: 'year_value',
    placeholder: 'year_placeholder',
    className: 'tw-max-w-[140px]',
  },
  cvv: {
    type: 'fieldWrapper',
    name: 'cvv',
    Component: CardCvvElement,
    label: 'security_code',
    placeholder: 'cvv_placeholder',
    fieldWrapperClass: 'tw-grow',
  },
  country: {
    type: 'controller',
    name: 'country',
    label: 'country',
    className: '!tw-h-9',
  },
  postalCode: {
    type: 'input',
    name: 'postalCode',
    dataRecurly: RECURLY_FORM_FIELDS.POSTAL_CODE,
    label: 'billing_postal_code',
    placeholder: 'billing_postal_code_placeholder',
  },
}

export const RECURLY_FORM_CONFIG: (InputConfig | InputConfig[])[] = [
  [RECURLY_INPUTS.firstName, RECURLY_INPUTS.lastName],
  RECURLY_INPUTS.cardNumber,
  [RECURLY_INPUTS.expiryMonth, RECURLY_INPUTS.expiryYear, RECURLY_INPUTS.cvv],
  [
    {
      type: 'parent',
      className: 'tw-items-baseline',
    },
    RECURLY_INPUTS.country,
    RECURLY_INPUTS.postalCode,
  ],
]

export type CardBrand =
  | 'amex'
  | 'discover'
  | 'mastercard'
  | 'visa'
  | 'diners'
  | 'jcb'
  | 'unionpay'
  | 'cartes_bancaires'

export const CARD_FLAGS: Record<CardBrand, string> = {
  amex,
  discover,
  mastercard,
  visa,
  diners,
  jcb,
  unionpay,
  cartes_bancaires,
}

export const CHECKOUT_SUBMIT_TYPE = {
  update: 'update-billing-info',
}
